// SOVRANOVA — Spoken-guidance voice picker (ElevenLabs).
// Sets the voice SOVRANOVA narrates with: greetings, guidance, affirmations,
// hypnosis, and crisis support. (Live voice conversations use the Hume voice.)
// Persists via useSettings/localStorage; api.js auto-injects the chosen voice
// on every /voice/speak call, so no other surface needs to change.
import { useState } from 'react'
import { useSettings } from '../../hooks/useSettings'
import { api } from '../../services/api'
import { VOICES } from '../../services/voices'

export function VoicePicker() {
  const { settings, setSetting } = useSettings()
  const [previewing, setPreviewing] = useState(null)
  const disabled = settings.textOnly
  const selected = settings.voiceId || VOICES[0].id

  const preview = async (voiceId) => {
    if (previewing) return
    setPreviewing(voiceId)
    try {
      const res = await api.post('/voice/speak', { text: "Hi, I'm here with you. This is how I'll sound.", voiceId })
      if (res?.audio) {
        const bin = atob(res.audio); const bytes = new Uint8Array(bin.length)
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
        const audio = new Audio(URL.createObjectURL(new Blob([bytes], { type: res.mimeType || 'audio/mpeg' })))
        audio.onended = () => setPreviewing(null)
        audio.onerror = () => setPreviewing(null)
        await audio.play()
      } else { setPreviewing(null) }
    } catch { setPreviewing(null) }
  }

  return (
    <div>
      <p style={S.label}>SPOKEN GUIDANCE VOICE</p>
      <p style={S.hint}>
        The voice SOVRANOVA uses for greetings, guidance, affirmations, hypnosis and crisis support.
        Live voice conversations use the Hume companion voice.
      </p>
      {VOICES.map(v => {
        const active = selected === v.id
        return (
          <div key={v.id} style={{ ...S.row, ...(active ? S.rowActive : {}) }}>
            <button onClick={() => setSetting('voiceId', v.id)} disabled={disabled} style={S.pick} aria-pressed={active}>
              <span style={S.name}>{v.name}{active ? ' ✓' : ''} <span style={S.gender}>· {v.gender}</span></span>
              <span style={S.tone}>{v.tone}</span>
            </button>
            <button onClick={() => preview(v.id)} disabled={disabled || !!previewing} style={S.play} aria-label={`Preview ${v.name}`}>
              {previewing === v.id ? '♪' : '▶'}
            </button>
          </div>
        )
      })}
    </div>
  )
}

const S = {
  // Organic contrast fix (2026-08-14): this list sits on the cream Settings
  // drawer — dark warm ink text, never the old dark-theme light values.
  label: { fontSize:'0.65rem', fontWeight:700, letterSpacing:'0.12em', color:'var(--og-a700, #8c491a)', margin:'1.25rem 0 0.5rem' },
  hint:  { fontSize:'0.74rem', color:'var(--og-n700, #645c50)', margin:'0 0 0.6rem', lineHeight:1.55 },
  row:   { display:'flex', alignItems:'center', gap:'0.5rem', padding:'0.4rem 0', borderBottom:'1.5px solid var(--og-n200, #eee7db)' },
  rowActive: { background:'var(--og-a100, #fff2eb)', borderRadius:'0.9rem' },
  pick:  { flex:1, textAlign:'left', background:'transparent', border:'none', cursor:'pointer', display:'flex', flexDirection:'column', gap:'0.15rem', padding:'0.4rem 0.5rem', fontFamily:'inherit' },
  name:  { fontSize:'0.92rem', fontWeight:600, color:'var(--og-n900, #2e2b25)' },
  gender:{ fontSize:'0.72rem', fontWeight:500, color:'var(--og-a700, #8c491a)' },
  tone:  { fontSize:'0.74rem', color:'var(--og-n700, #645c50)' },
  play:  { width:34, height:34, flexShrink:0, borderRadius:'50%', border:'1.5px solid var(--og-n300, #dcd3c4)', background:'var(--og-n100, #f9f4ed)', color:'var(--og-a800, #643312)', cursor:'pointer', fontSize:'0.8rem' },
}
